import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { alpha } from "@mui/material";
import { DataContext } from "../index";
import TvChartContainer from "../TVChart";

const DEFAULT_SLOTS = [
  { id: "slot_0", currencycode: "BTC", basecurrency: "USDT" },
  { id: "slot_1", currencycode: "ETH", basecurrency: "USDT" },
  { id: "slot_2", currencycode: "SOL", basecurrency: "USDT" },
  { id: "slot_3", currencycode: "BNB", basecurrency: "USDT" },
];

export const FIXED_CHART_CONTAINER_HEIGHT = "470px";

const MultiChartContainer = ({ layout = "1", onLayoutChange, btnColor: propBtnColor, isFullscreen = false }) => {
  const reduxBtnColor = useSelector((state) => state?.btncolor);
  const themeBtnColor = propBtnColor || reduxBtnColor || "#1976d2";
  const { currencycode, basecurrency, setCurrencycode, setBasecurrency } =
    useContext(DataContext) || {};

  const [activeSlotIndex, setActiveSlotIndex] = useState(0);
  const isInternalChangeRef = useRef(false);

  // Load / initialize slot symbols
  const [slots, setSlots] = useState(() => {
    try {
      const cached = localStorage.getItem("spot_multichart_slots");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length >= 4) {
          return parsed;
        }
      }
    } catch (e) {}
    return DEFAULT_SLOTS;
  });

  // When external currency changes (e.g. from LeftTable dropdown), update ONLY the active slot
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    if (currencycode && basecurrency) {
      setSlots((prev) => {
        const next = [...prev];
        const currentSlot = next[activeSlotIndex];
        if (
          currentSlot?.currencycode?.toUpperCase() !== currencycode?.toUpperCase() ||
          currentSlot?.basecurrency?.toUpperCase() !== basecurrency?.toUpperCase()
        ) {
          next[activeSlotIndex] = {
            ...next[activeSlotIndex],
            currencycode: currencycode,
            basecurrency: basecurrency,
          };
          return next;
        }
        return prev;
      });
    }
  }, [currencycode, basecurrency, activeSlotIndex]);

  // Persist slots configuration
  useEffect(() => {
    try {
      localStorage.setItem("spot_multichart_slots", JSON.stringify(slots));
    } catch (e) {}
  }, [slots]);

  // Automatically dispatch window resize when multichart layout changes so TradingView recalculates bounds
  useEffect(() => {
    const t1 = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 100);
    const t2 = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [layout]);

  // Handle changing symbol for a specific slot
  const handleSlotSymbolChange = (slotIdx, newCur, newBase) => {
    setSlots((prev) => {
      const next = [...prev];
      next[slotIdx] = {
        ...next[slotIdx],
        currencycode: newCur,
        basecurrency: newBase,
      };
      return next;
    });

    // If active slot, also update the main spot context so Orderbook / BuySell syncs
    if (slotIdx === activeSlotIndex && setCurrencycode && setBasecurrency) {
      isInternalChangeRef.current = true;
      setCurrencycode(newCur);
      setBasecurrency(newBase);
      localStorage.setItem("buying_crypto_asset", newCur.toLowerCase());
      localStorage.setItem("selling_crypto_asset", newBase.toLowerCase());
    }
  };

  // Handle clicking / activating a slot
  const handleActivateSlot = (slotIdx) => {
    setActiveSlotIndex(slotIdx);
    const targetSlot = slots[slotIdx];
    if (targetSlot && setCurrencycode && setBasecurrency) {
      if (
        targetSlot.currencycode?.toUpperCase() !== currencycode?.toUpperCase() ||
        targetSlot.basecurrency?.toUpperCase() !== basecurrency?.toUpperCase()
      ) {
        isInternalChangeRef.current = true;
        setCurrencycode(targetSlot.currencycode);
        setBasecurrency(targetSlot.basecurrency);
        localStorage.setItem("buying_crypto_asset", targetSlot.currencycode.toLowerCase());
        localStorage.setItem("selling_crypto_asset", targetSlot.basecurrency.toLowerCase());
      }
    }
  };

  // Determine how many slots to display based on layout
  const visibleSlotCount = useMemo(() => {
    if (layout === "1") return 1;
    if (layout.startsWith("2")) return 2;
    if (layout.startsWith("3")) return 3;
    if (layout.startsWith("4")) return 4;
    return 1;
  }, [layout]);

  // Height and grid styling calculation
  const { gridStyle, getSlotItemStyle } = useMemo(() => {
    const baseSlot = {
      height: "100%",
      width: "100%",
      minHeight: 0,
      minWidth: 0,
    };

    switch (layout) {
      case "1":
        return {
          gridStyle: {
            display: "block",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "2-cols":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, 1fr)",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "2-rows":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gridTemplateRows: "repeat(2, minmax(0, 1fr))",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "3-cols":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, 1fr)",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "3-rows":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gridTemplateRows: "repeat(3, minmax(0, 1fr))",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "3-top-split":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, 1.2fr) minmax(0, 1fr)",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: (idx) => ({
            ...baseSlot,
            gridColumn: idx === 0 ? "1 / span 2" : undefined,
          }),
        };

      case "3-bottom-split":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, 1fr) minmax(0, 1.2fr)",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: (idx) => ({
            ...baseSlot,
            gridColumn: idx === 2 ? "1 / span 2" : undefined,
          }),
        };

      case "4-grid":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gridTemplateRows: "repeat(2, minmax(0, 1fr))",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "4-cols":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, 1fr)",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "4-left-main":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.8fr) minmax(0, 1.2fr)",
            gridTemplateRows: "repeat(3, minmax(0, 1fr))",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: (idx) => ({
            ...baseSlot,
            gridColumn: idx === 0 ? "1" : "2",
            gridRow: idx === 0 ? "1 / span 3" : undefined,
          }),
        };

      case "4-top-main":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, 1.2fr) minmax(0, 1fr)",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: (idx) => ({
            ...baseSlot,
            gridColumn: idx === 0 ? "1 / span 3" : undefined,
            gridRow: idx === 0 ? "1" : "2",
          }),
        };

      default:
        return {
          gridStyle: { display: "block", width: "100%", height: "100%", minHeight: 0, minWidth: 0 },
          getSlotItemStyle: () => baseSlot,
        };
    }
  }, [layout]);

  // Render single chart mode (standard)
  if (layout === "1") {
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          position: "relative",
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TvChartContainer
          currencycode={currencycode}
          basecurrency={basecurrency}
          instanceId="main"
          height="100%"
          showHeader={false}
        />
      </div>
    );
  }

  // Render multi-chart grid
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        padding: "4px",
        boxSizing: "border-box",
        backgroundColor: "var(--body-color-less, #181a20)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ ...gridStyle, height: "100%", width: "100%", minHeight: 0, minWidth: 0, boxSizing: "border-box" }}>
        {slots.slice(0, visibleSlotCount).map((slot, idx) => {
          const isActive = idx === activeSlotIndex;
          const slotStyle = getSlotItemStyle(idx);

          return (
            <div
              key={slot.id}
              onClick={() => handleActivateSlot(idx)}
              style={{
                ...slotStyle,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                minHeight: 0,
                minWidth: 0,
                borderRadius: "8px",
                border: isActive
                  ? `1.5px solid ${themeBtnColor}`
                  : "1px solid rgba(125, 125, 125, 0.2)",
                boxShadow: isActive
                  ? `0 0 10px ${alpha(themeBtnColor, 0.25)}`
                  : "none",
                backgroundColor: "var(--body-color, #1e2329)",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                position: "relative",
                boxSizing: "border-box",
              }}
            >
              <TvChartContainer
                currencycode={slot.currencycode}
                basecurrency={slot.basecurrency}
                instanceId={slot.id}
                height="100%"
                showHeader={true}
                isActive={isActive}
                btnColor={themeBtnColor}
                onActivate={() => handleActivateSlot(idx)}
                onSymbolChange={(newCur, newBase) =>
                  handleSlotSymbolChange(idx, newCur, newBase)
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MultiChartContainer;
